import { checkDomain, checkDomainReachability, fetchDomain } from '@/api/domains';
import LoadingWrapper from '@/components/loading-wrapper';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import type { IUserDomain } from '@/entities/domain.entity';
import type { IBuild } from '@/entities/build.entity';
import { PageScroll } from '@/layouts/page-layout';
import { CopyIcon, GlobeIcon, RefreshCwIcon } from 'lucide-react';
import { useCallback, useEffect, useState, type MouseEvent } from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';
import DomainBuildsList from './domain-builds-list';
import DomainStatusBadge from './domain-status-badge';

export default function DomainDetailPage() {
	const { id } = useParams<{ id: string }>();
	const domainId = Number(id);

	const [domain, setDomain] = useState<IUserDomain | null>(null);
	const [builds, setBuilds] = useState<IBuild[]>([]);
	const [loading, setLoading] = useState(true);
	const [isChecking, setIsChecking] = useState(false);
	const [isReachabilityChecking, setIsReachabilityChecking] = useState(false);

	const loadDomain = useCallback(async () => {
		if (!Number.isFinite(domainId)) return;
		setLoading(true);
		try {
			const data = (await fetchDomain(domainId)) as Awaited<ReturnType<typeof fetchDomain>> & {
				launchCount: number;
			};
			setDomain({ ...data.domain, launchCount: data.launchCount });
			setBuilds(data.builds);
		} finally {
			setLoading(false);
		}
	}, [domainId]);

	useEffect(() => {
		void loadDomain();
	}, [loadDomain]);

	function handleCopy(e: MouseEvent, text: string) {
		e.stopPropagation();
		navigator.clipboard
			.writeText(text)
			.then(() => toast.success('Скопировано'))
			.catch(() => toast.error('Ошибка копирования'));
	}

	async function handleCheck() {
		if (!domain) return;
		setIsChecking(true);
		try {
			const updated = await checkDomain(domain.id);
			setDomain(prev => (prev ? { ...updated, launchCount: prev.launchCount } : prev));
			toast.success('Проверка DNS запущена');
		} finally {
			setIsChecking(false);
		}
	}

	async function handleReachabilityCheck() {
		if (!domain) return;
		setIsReachabilityChecking(true);
		try {
			const updated = await checkDomainReachability(domain.id);
			setDomain(prev => (prev ? { ...updated, launchCount: prev.launchCount } : prev));
			toast.success('Проверка доступности завершена');
		} finally {
			setIsReachabilityChecking(false);
		}
	}

	if (loading) {
		return (
			<PageScroll contentClassName='p-2 flex justify-center py-16'>
				<Spinner />
			</PageScroll>
		);
	}

	if (!domain) {
		return (
			<PageScroll contentClassName='p-2'>
				<div className='text-center text-sm text-muted-foreground py-16'>Домен не найден</div>
			</PageScroll>
		);
	}

	return (
		<PageScroll contentClassName='p-2 flex flex-col gap-3'>
			<Card className='gap-0 py-0 shadow-sm overflow-hidden'>
				<CardHeader className='border-b px-4 py-4 flex'>
					<div className='flex items-start justify-between gap-3 w-full'>
						<div className='flex items-center gap-3 min-w-0'>
							<div className='rounded-lg bg-chart-1/15 p-2 shrink-0'>
								<GlobeIcon className='text-chart-1' size={20} />
							</div>
							<div className='min-w-0'>
								<CardTitle className='text-chart-1 truncate'>{domain.hostname}</CardTitle>
								<CardDescription>Зона: {domain.zoneName}</CardDescription>
							</div>
						</div>
						<DomainStatusBadge status={domain.status} />
					</div>
				</CardHeader>

				<CardContent className='px-4 py-4 flex flex-col gap-4'>
					<div className='grid grid-cols-2 gap-2 text-sm'>
						<div className='rounded-lg border border-border bg-muted/30 px-3 py-2'>
							<p className='text-xs text-muted-foreground'>Сборок на домене</p>
							<p className='font-medium'>{domain.buildsCount ?? builds.length}</p>
						</div>
						<div className='rounded-lg border border-border bg-muted/30 px-3 py-2'>
							<p className='text-xs text-muted-foreground'>Установок (launch)</p>
							<p className='font-medium'>{domain.launchCount}</p>
						</div>
					</div>

					{domain.errorMessage ? (
						<div className='rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive'>
							{domain.errorMessage}
						</div>
					) : null}

					{domain.nsRecords && domain.nsRecords.length > 0 ? (
						<div className='flex flex-col gap-2'>
							<span className='text-sm font-medium'>NS-записи</span>
							<p className='text-xs text-muted-foreground'>Укажите эти NS у регистратора домена</p>
							{domain.nsRecords.map(ns => (
								<div key={ns} className='flex items-center justify-between gap-2 rounded-md border border-border bg-muted/30 px-3 py-2'>
									<code className='truncate text-sm'>{ns}</code>
									<Button type='button' variant='ghost' size='icon' className='size-8 shrink-0' onClick={e => handleCopy(e, ns)}>
										<CopyIcon size={14} />
									</Button>
								</div>
							))}
						</div>
					) : null}

					<div className='flex flex-col gap-2'>
						<span className='text-sm font-medium'>Доступность снаружи</span>
						{domain.blockCheckResult ? (
							<p className='text-sm text-muted-foreground'>
								{domain.blockCheckResult.reachable ? (
									<span className='text-chart-2'>HTTPS /health OK</span>
								) : (
									<span className='text-destructive'>
										Недоступен{domain.blockCheckResult.error ? `: ${domain.blockCheckResult.error}` : ''}
									</span>
								)}
								{domain.lastBlockCheckAt ? (
									<span className='block text-xs mt-1'>
										Проверено: {new Date(domain.lastBlockCheckAt).toLocaleString('ru-RU')}
									</span>
								) : null}
							</p>
						) : (
							<p className='text-xs text-muted-foreground'>Ещё не проверялось</p>
						)}
						<LoadingWrapper show={isReachabilityChecking}>
							<Button
								type='button'
								variant='outline'
								className='self-start'
								disabled={isReachabilityChecking}
								onClick={() => void handleReachabilityCheck()}
							>
								<RefreshCwIcon size={14} className='mr-1' />
								Проверить доступность
							</Button>
						</LoadingWrapper>
					</div>

					<LoadingWrapper show={isChecking}>
						<Button type='button' variant='outline' className='self-start' disabled={isChecking} onClick={() => void handleCheck()}>
							<RefreshCwIcon size={14} className='mr-1' />
							Проверить DNS
						</Button>
					</LoadingWrapper>
				</CardContent>
			</Card>

			<DomainBuildsList builds={builds} onInstalled={() => void loadDomain()} />
		</PageScroll>
	);
}
